import type { DepositType } from '@api';
import { formatCurrency, formatDate, LinkableEntity } from '@common';
import { DataTableColumn } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import {
  DepositBankAccountLabel,
  DepositSourceLink,
  DepositStatusBadge,
} from './components';

const COL = 'DEPOSITS_TABLE.COLUMNS';
const ENUM = 'DEPOSITS_TABLE.ENUMS';

type UseDepositsTableColumnsReturnType = {
  columns: DataTableColumn<DepositType>[];
};

export const useDepositsTableColumns =
  (): UseDepositsTableColumnsReturnType => {
    const { t } = useTranslation('financeDeposits');

    const columns: DataTableColumn<DepositType>[] = [
      {
        accessor: 'artist',
        sortable: true,
        title: t(`${COL}.ARTIST`),
        width: 200,
        render: ({ artist }) => <LinkableEntity linkableEntity={artist} />,
      },
      {
        accessor: 'project',
        sortable: true,
        title: t(`${COL}.PROJECT`),
        width: 200,
      },
      {
        accessor: 'module',
        title: t(`${COL}.MODULE`),
        width: 140,
        render: ({ module }) => t(`${ENUM}.MODULE.${module}`),
      },
      {
        accessor: 'subjectType',
        title: t(`${COL}.SUBJECT_TYPE`),
        width: 160,
        render: ({ subjectType }) => t(`${ENUM}.SUBJECT_TYPE.${subjectType}`),
      },
      {
        accessor: 'dueDate',
        sortable: true,
        title: t(`${COL}.DUE_DATE`),
        width: 120,
        render: ({ dueDate }) => formatDate(dueDate),
      },
      {
        accessor: 'status',
        sortable: true,
        title: t(`${COL}.STATUS`),
        width: 120,
        render: ({ status }) => <DepositStatusBadge status={status} />,
      },
      {
        accessor: 'contractingParty',
        title: t(`${COL}.CONTRACTING_PARTY`),
        width: 200,
        render: ({ contractingParty }) => (
          <LinkableEntity linkableEntity={contractingParty} />
        ),
      },
      {
        accessor: 'agency',
        sortable: true,
        title: t(`${COL}.AGENCY`),
        width: 200,
        render: ({ agency }) => <LinkableEntity linkableEntity={agency} />,
      },
      {
        accessor: 'bankAccount',
        title: t(`${COL}.BANK_ACCOUNT`),
        width: 220,
        render: ({ bankAccount }) => (
          <DepositBankAccountLabel bankAccount={bankAccount} />
        ),
      },
      {
        accessor: 'currency',
        sortable: true,
        title: t(`${COL}.CURRENCY`),
        width: 160,
        render: ({ depositGross }) => depositGross.currency,
      },
      {
        accessor: 'depositGross',
        title: t(`${COL}.DEPOSIT_GROSS`),
        width: 160,
        render: ({ depositGross }) => formatCurrency(depositGross),
      },
      {
        accessor: 'id',
        title: t(`${COL}.LINK`),
        titleStyle: { textAlign: 'center' },
        width: 100,
        render: (deposit) => <DepositSourceLink deposit={deposit} />,
      },
    ];

    return { columns };
  };
